// Partner invites wherever you are on the site (Paul, 18 Oct 2026: "invite a partner conveniently that
// might be online now ... shouldn't need emailing or exiting the site").
// Mounted once in App.js for every page. While signed in it asks the partnerHub function every few
// seconds (only while the tab is visible) whether anyone has invited you to play, and pops up a card:
// Join takes you straight to the table. The inviter gets a card when the answer comes back.
// It also returns someone who opened an invite link while signed out, then signed in from the site's
// own sign-in page, to that invite once. Sign-in and sign-up themselves are untouched.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { ph, testUid, takeRememberedCode } from "./phApi";
import { Avatar } from "./phUi";
import "./PartnerHub.css";

const POLL_MS = 10000;
const PING_EVERY = 3; // mark "on the site" every third poll (~30s)
const WAIT_ON = /^\/(login|signup|complete-profile|success|subscribe|membership|partner)/;
const NO_POPUP = /^\/(login|signup|complete-profile|success|subscribe)/;

function PartnerInbox({ uid, location, history }) {
  const signedIn = !!uid || !!testUid();
  const [incoming, setIncoming] = useState([]);
  const [answered, setAnswered] = useState([]);
  const [bidding, setBidding] = useState([]); // Partnership bidding sets a partner started with you
  const [dismissed, setDismissed] = useState(() => new Set());
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const tick = useRef(0);

  // An invite link opened while signed out.
  useEffect(() => {
    if (!uid || WAIT_ON.test(location.pathname)) return;
    let code = null;
    try { code = localStorage.getItem("ph-join-code"); } catch (e) { /* storage blocked */ }
    if (!code || !/^[A-Z0-9]{4,10}$/.test(code)) return;
    takeRememberedCode();
    history.replace(`/partner/join/${code}`);
  }, [uid, location.pathname, history]);

  const poll = useCallback(async () => {
    if (typeof document !== "undefined" && document.hidden) return;
    const ping = tick.current % PING_EVERY === 0;
    tick.current += 1;
    try {
      const r = await ph("inbox", { ping });
      setIncoming(r.incoming || []);
      setAnswered(r.answered || []);
      setBidding(r.bidding || []);
    } catch (e) {
      /* signed out or offline: try again next time */
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return undefined;
    poll();
    const t = setInterval(poll, POLL_MS);
    const onShow = () => { if (!document.hidden) poll(); };
    document.addEventListener("visibilitychange", onShow);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onShow); };
  }, [signedIn, poll]);

  // Sending an invite from the hub or the table refreshes this at once (no 10s wait for the answer).
  useEffect(() => {
    const now = () => poll();
    window.addEventListener("pth-inbox-refresh", now);
    return () => window.removeEventListener("pth-inbox-refresh", now);
  }, [poll]);

  if (!signedIn || NO_POPUP.test(location.pathname)) return null;

  const tablePath = (pairId) => `/partner/${pairId}/weekly`;
  // "partner" = an invite to be partners; "play" = come to the weekly table now. Each says what was sent.
  const kindOf = (x) => x.kind || (x.pairId ? "play" : "partner");
  const hubPath = (pairId) => `/partner?with=${encodeURIComponent(pairId)}`;
  const onTable = (pairId) => location.pathname === tablePath(pairId);
  const key = (x) => `${x.id}:${x.at || 0}`; // the same pair reuses one record, so each send is its own card
  const eventPath = (x) => `/events/${x.eventId}`;
  const inv = incoming.find((x) => !dismissed.has(key(x)) && !(kindOf(x) === "play" && x.pairId && onTable(x.pairId)) && !(kindOf(x) === "event" && location.pathname === eventPath(x)));
  const ans = answered.find((x) => !dismissed.has(key(x)));
  // Partnership bidding (Paul, 18 Oct 2026): "<name> started a bidding set with you". Not the weekly.
  const setPath = (setId) => `/partner-bidding/${setId}`;
  const bid = !inv && bidding.find((x) => !dismissed.has(`pb:${x.id}`) && !location.pathname.startsWith(setPath(x.setId)));
  if (!inv && !ans && !bid) return null;

  const hide = (x) => setDismissed((s) => new Set(s).add(key(x)));
  const answer = async (accept) => {
    setBusy(accept ? "join" : "no");
    setErr("");
    try {
      const r = await ph("answerLive", { id: inv.id, accept });
      hide(inv);
      if (accept && kindOf(inv) === "event") history.push(eventPath(inv));
      else if (accept && r.pairId) history.push(kindOf(inv) === "play" ? tablePath(r.pairId) : hubPath(r.pairId));
      if (accept) window.dispatchEvent(new CustomEvent("pth-hub-refresh", { detail: { pairId: r.pairId } }));
    } catch (e) {
      setErr(e.message);
      hide(inv);
    }
    setBusy("");
  };
  const seeSet = (go) => {
    setDismissed((d) => new Set(d).add(`pb:${bid.id}`));
    ph("seenNotice", { id: bid.id }).catch(() => {});
    if (go) history.push(setPath(bid.setId));
  };
  const ack = (go) => {
    hide(ans);
    ph("ackLive", { id: ans.id }).catch(() => {});
    if (go && ans.pairId) history.push(kindOf(ans) === "play" ? tablePath(ans.pairId) : hubPath(ans.pairId));
  };

  return (
    <div className="pth-pop" role="dialog" aria-live="polite" aria-label={inv ? (kindOf(inv) === "event" ? "Event entry" : kindOf(inv) === "play" ? "Weekly tournament invite" : "Partner invite") : bid ? "Partnership bidding set" : "Invite answer"}>
      {inv ? (
        <>
          <div className="pth-popTop">
            <Avatar name={inv.fromName} online size="sm" />
            <div className="pth-popTxt">
              <b>{inv.fromName}</b> {kindOf(inv) === "event" ? <>has entered you both in <b>{inv.eventName || "an event"}</b></> : kindOf(inv) === "play" ? "wants to play the weekly tournament with you" : "has invited you to be partners"}
            </div>
          </div>
          {err && <p className="pth-err">{err}</p>}
          <div className="pth-popBtns">
            <button type="button" className="pth-btn pth-btn--primary pth-btn--sm" disabled={!!busy} onClick={() => answer(true)}>{busy === "join" ? "Opening…" : kindOf(inv) === "event" ? "See the event" : kindOf(inv) === "play" ? "Join the weekly tournament" : "Accept"}</button>
            <button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" disabled={!!busy} onClick={() => answer(false)}>Not now</button>
          </div>
        </>
      ) : bid ? (
        <>
          <div className="pth-popTop">
            <Avatar name={bid.fromName} online size="sm" />
            <div className="pth-popTxt"><b>{bid.fromName}</b> started a bidding set with you</div>
          </div>
          <div className="pth-popBtns">
            <button type="button" className="pth-btn pth-btn--primary pth-btn--sm" onClick={() => seeSet(true)}>Go to the set</button>
            <button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" onClick={() => seeSet(false)}>Not now</button>
          </div>
        </>
      ) : (
        <>
          <div className="pth-popTop">
            <Avatar name={ans.name} online={ans.accepted} size="sm" />
            <div className="pth-popTxt">
              <b>{ans.name}</b> {kindOf(ans) === "event" ? (ans.accepted ? "has seen the event entry" : "saw the event entry") : ans.accepted ? (kindOf(ans) === "play" ? "is coming to the weekly tournament table" : "accepted your invite to be partners") : kindOf(ans) === "play" ? "can't play the weekly tournament just now" : "said not now"}
            </div>
          </div>
          <div className="pth-popBtns">
            {ans.accepted && ans.pairId && kindOf(ans) === "play" && !onTable(ans.pairId) && (
              <button type="button" className="pth-btn pth-btn--primary pth-btn--sm" onClick={() => ack(true)}>Go to the weekly tournament table</button>
            )}
            {ans.accepted && ans.pairId && kindOf(ans) === "partner" && location.pathname !== "/partner" && (
              <button type="button" className="pth-btn pth-btn--primary pth-btn--sm" onClick={() => ack(true)}>Go to your partners</button>
            )}
            <button type="button" className="pth-btn pth-btn--quiet pth-btn--sm" onClick={() => ack(false)}>OK</button>
          </div>
        </>
      )}
    </div>
  );
}

export default withRouter(connect((state) => ({ uid: state.auth?.uid }))(PartnerInbox));
